/**
 * CrmEntitySmartSearch — 1 ô search tìm chéo Company / Account (Shop) / Contact / Deal,
 * kết quả hiện thành chips có icon type. Mỗi type chỉ chọn được 1 (tương ứng crm_links).
 *
 * Props:
 *  - value: { company_id, account_id, contact_id, deal_id }
 *  - onChange: (next) => void
 *  - prefillItems: [{ entity_type, id, label, sub }] — optional, để skip resolve API khi đã có label
 *  - autoResolve: bool — default true, gọi /crm/entities/resolve khi value có id mà chưa có label
 *  - types: array — default ['company','account','contact','deal']
 *  - singleEntity: bool — khi true, pick 1 type sẽ clear tất cả type khác (mutex). Dùng cho
 *    pattern "đối tác = công ty HOẶC liên hệ".
 *  - partnerOnly: bool — chỉ search trong pool partner (relationship_type='partner').
 *    Truyền xuống BE qua param partner_only=true.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Search, X, Loader2, Building2, Store, Users, Briefcase, Target } from 'lucide-react';
import api from '@host/axios';

import { Trans, useLingui } from '@lingui/react/macro';

import { msg } from '@lingui/core/macro';import { tMeta, i18n } from '../../i18n/runtime.js';

const TYPE_META = {
  company: { label: msg`Công ty`,   key: 'company_id', Icon: Building2, cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  account: { label: 'Shop',      key: 'account_id', Icon: Store,     cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  contact: { label: msg`Liên hệ`,   key: 'contact_id', Icon: Users,     cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  deal:    { label: 'Deal',      key: 'deal_id',    Icon: Briefcase, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  lead:    { label: 'Lead',      key: 'lead_id',    Icon: Target,    cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};
// Nơi hiện LẠI bản ghi đã chọn (vd chi tiết đơn «Bằng chứng ra ngoài») dùng đúng nhãn/màu/icon của ô chọn.
// Nhãn có thể là MessageDescriptor ⇒ đọc qua tMeta().
export { TYPE_META as CRM_ENTITY_TYPE_META };

export default function CrmEntitySmartSearch({
  value = {},
  onChange,
  prefillItems = [],
  autoResolve = true,
  types = ['company', 'account', 'contact', 'deal'],
  placeholder = i18n._(msg`Tìm công ty, shop, liên hệ, deal...`),
  singleEntity = false,
  partnerOnly = false,
  // relatedOnly: chỉ search bản ghi user phụ trách/theo dõi (BE param related_only —
  // hiện áp cho lead/deal). Dùng cho field out_evidence: link lead người khác sẽ bị
  // BE chặn lúc nộp đơn, nên lọc từ search luôn.
  relatedOnly = false,
  // onPickItem: (item {entity_type, id, label, sub} | null) — bắn kèm onChange khi
  // chọn/bỏ chọn, cho caller cần cả label chứ không chỉ id (vd field out_evidence).
  onPickItem,
}) {
  const {
    t
  } = useLingui();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  // chipMap: { company: {id, label, sub} | null, ... }
  const [chipMap, setChipMap] = useState(() => {
    const m = {};
    for (const it of prefillItems) m[it.entity_type] = it;
    return m;
  });
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const portalRef = useRef(null);

  // Resolve labels khi value có id mà chưa có chip
  useEffect(() => {
    if (!autoResolve) return;
    const need = [];
    for (const t of types) {
      const id = value?.[tMeta(TYPE_META[t]).key];
      if (id && !chipMap[t]) need.push(`${t}:${id}`);
    }
    if (need.length === 0) return;
    api.get('/crm/entities/resolve', { params: { ids: need.join(',') } })
      .then(r => {
        const items = r.data?.data || [];
        if (!items.length) return;
        setChipMap(prev => {
          const next = { ...prev };
          for (const it of items) next[it.entity_type] = it;
          return next;
        });
      })
      .catch(() => {});
  }, [value?.company_id, value?.account_id, value?.contact_id, value?.deal_id, value?.lead_id]);

  // Search debounced
  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get('/crm/entities/search', {
          params: { q: query, types: types.join(','), limit: 5, ...(partnerOnly ? { partner_only: 'true' } : {}), ...(relatedOnly ? { related_only: 'true' } : {}) },
        });
        setResults(r.data?.data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, query ? 250 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (portalRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = useCallback((item) => {
    if (singleEntity) {
      // Mutex: pick 1 → clear tất cả types khác
      setChipMap({ [item.entity_type]: item });
      const next = {};
      for (const t of types) next[tMeta(TYPE_META[t]).key] = t === item.entity_type ? item.id : '';
      onChange?.(next);
    } else {
      setChipMap(prev => ({ ...prev, [item.entity_type]: item }));
      onChange?.({ ...value, [tMeta(TYPE_META[item.entity_type]).key]: item.id });
    }
    onPickItem?.(item);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  }, [onChange, value, singleEntity, types, onPickItem]);

  const removeChip = useCallback((type) => {
    setChipMap(prev => { const n = { ...prev }; delete n[type]; return n; });
    onChange?.({ ...value, [tMeta(TYPE_META[type]).key]: '' });
    onPickItem?.(null);
  }, [onChange, value, onPickItem]);

  const chips = types.filter(t => chipMap[t]).map(t => ({ type: t, ...chipMap[t] }));

  // Position portal dropdown
  const getDropdownStyle = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return {};
    const below = window.innerHeight - r.bottom;
    return below > 320 || below > r.top
      ? { top: r.bottom + 4, left: r.left, width: r.width }
      : { top: r.top - 304, left: r.left, width: r.width };
  };

  return (
    <div ref={wrapRef} className="relative">
      <div
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
        className={`w-full min-h-[42px] flex flex-wrap items-center gap-1.5 px-2 py-1.5 border rounded-xl bg-white cursor-text transition ${
          open ? 'ring-2 ring-purple-500 border-purple-500' : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        {chips.map(c => {
          const meta = tMeta(TYPE_META[c.type]);
          const Icon = meta.Icon;
          return (
            <span key={c.type} className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-lg border text-xs font-medium ${meta.cls}`}>
              <Icon size={11} />
              <span className="opacity-60 mr-0.5">{meta.label}:</span>
              <span className="truncate max-w-[160px]">{c.label}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeChip(c.type); }}
                className="ml-0.5 p-0.5 rounded hover:bg-white/60"
                title={t`Bỏ chọn`}
              >
                <X size={11} />
              </button>
            </span>
          );
        })}
        <div className="flex items-center gap-1 flex-1 min-w-[140px]">
          <Search size={13} className="text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={chips.length === 0 ? placeholder : t`Thêm liên kết...`}
            className="flex-1 text-sm bg-transparent outline-none py-0.5"
          />
          {loading && <Loader2 size={13} className="text-purple-500 animate-spin" />}
        </div>
      </div>

      {open && ReactDOM.createPortal(
        <div ref={portalRef}
          className="fixed z-[99999] bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden"
          style={getDropdownStyle()}>
          <div className="max-h-72 overflow-y-auto py-1">
            {results.length === 0 && !loading && (
              <p className="text-center text-xs text-gray-400 py-6">
                {query ? t`Không tìm thấy "${query}"` : t`Gõ để tìm — hoặc xem gợi ý`}
              </p>
            )}
            {results.map(item => {
              const meta = tMeta(TYPE_META[item.entity_type]);
              if (!meta) return null;
              const Icon = meta.Icon;
              const taken = chipMap[item.entity_type]?.id === item.id;
              return (
                <button
                  key={`${item.entity_type}:${item.id}`}
                  type="button"
                  onClick={() => pick(item)}
                  disabled={taken}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left transition text-sm ${
                    taken ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-50'
                  }`}
                >
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border ${meta.cls}`}>
                    <Icon size={13} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-gray-800 font-medium">{item.label}</p>
                    <p className="text-[10px] text-gray-400 truncate">
                      <span className="uppercase tracking-wide">{meta.label}</span>
                      {item.sub ? ` · ${item.sub}` : ''}
                    </p>
                  </div>
                  {taken && <span className="text-[10px] text-gray-400"><Trans>đã chọn</Trans></span>}
                </button>
              );
            })}
          </div>
          {chips.length > 0 && (
            <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-500">
              <Trans>Mỗi loại chỉ chọn được 1. Bỏ chips trên để chọn item khác cùng loại.</Trans>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
